/**
 * The palette runs the sky is painted from, and the evidence each one rests on.
 *
 * Split out of `sky-run.ts` — that module is the *mechanism* (what a run is,
 * how a band is chosen, which run an hour takes) and this one is the *data*.
 * Only one of the four is recovered; the split keeps the recovered one's
 * evidence next to it rather than buried among three port extensions.
 *
 * Every colour here is checked against `data/baked/palettes/BARON.PAL.json` by
 * `apps/web/test/time-of-day.test.ts`, so none of it can drift from the file.
 */

import type { PaletteColour } from "../world/terrain-palette.ts";

/**
 * `BARON.PAL` 48-63, the run `setSkyShades(48, 63, ds:0x796)` walks — the only
 * sky ramp in this game that is recovered rather than chosen
 * (`docs/engineering/enhanced-sky.md` §2.6: two call sites, both with constant
 * arguments). Zenith first, per this port's reading of which end is which,
 * which is `TREATED_LOOKS`'s to justify and not this constant's.
 *
 * These 16 entries are why the run is a *ramp* and not a gradient: red climbs
 * `125, 125, 130, 134, 138, 142 …` in fours while blue holds at 190 for five
 * entries and then at 186 for five more. No interpolation between the two ends
 * passes through that, which is the whole of G18.
 */
export const DAYTIME_SKY_RAMP: readonly number[] = [48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63];

/**
 * `ds:0x796` — how many of the original's **scanlines** each entry of the
 * 48-63 run gets, 69 in total. `overcast-sky.ts` recovered the sixteen
 * `int16`s from `RB.EXE`; this is where they are drawn.
 *
 * ## Which end is which, measured
 *
 * The table's orientation is not argued from what haze looks like — it is read
 * off four DOSBox-X captures of `RB.EXE` in
 * flight (`re-sessions/smoke/shots/{r1,v2,v3,k_F4}.png`, 640x400 of a 320x200
 * screen). Converted to scanlines, against the table:
 *
 * ```
 *  idx 49  row 0        1 (clipped)    table 6      idx 57  rows 37..40   4   table 4
 *  idx 50  rows 1..6    6              table 6      idx 58  rows 41..44   4   table 4
 *  idx 51  rows 7..12   6              table 6      idx 59  rows 45..47   3   table 3
 *  idx 52  rows 13..17  5              table 5      idx 60  rows 48..50   3   table 3
 *  idx 53  rows 18..22  5              table 5      idx 61  rows 51..53   3   table 3
 *  idx 54  rows 23..27  5              table 5      idx 62  rows 54..55   2   table 2
 *  idx 55  rows 28..32  5              table 5      idx 63  rows 56..57   2   table 2
 *  idx 56  rows 33..36  4              table 4      --      row 58        ground, 143
 * ```
 *
 * **Every unclipped band matches its table entry exactly**, which settles
 * three things at once:
 *
 * 1. **Entry `k` is palette index `48 + k`.** No reversal anywhere.
 * 2. **48 is the zenith and 63 the horizon** — indices ascend *downward* and
 *    index 63's band is the last thing above the ground. The port's inference
 *    was right, and it is no longer an inference.
 * 3. **The run is anchored at the horizon, not at the top of the window.** In
 *    these frames the horizon sat at scanline 58 and the run is 69 tall, so the
 *    first eleven rows were clipped off the top of the screen — which is
 *    exactly the 11 missing rows observed. A run that started at row 0 could not
 *    do that.
 *
 * A second witness is tracked in this repo rather than untracked beside it:
 * `docs/game/ui-reference/view-airplanes-3d.png`, the original's *View
 * Airplanes in 3D* screen, shows the same 48-at-the-top ordering under
 * `PS.EXE`'s own (uniform, 4-scanline) table.
 *
 * ## What is still open
 *
 * The run covers 69 scanlines and a 320x200 sky is 81 rows deep, so **twelve
 * rows above the run are unaccounted for** and nothing measured says what fills
 * them. This port fills them with the zenith entry, which is what clamping the
 * ramp at its own end does; that is a choice, not a finding.
 */
export const SKY_BAND_SCANLINES: readonly number[] = [6, 6, 6, 6, 5, 5, 5, 5, 4, 4, 4, 3, 3, 3, 2, 2];

/**
 * How far above the horizon the daytime run reaches — **the one number in the
 * recovered path that is not recovered**, and it is stated in degrees for
 * `lessons.md`'s reason: an exponent has no wrong-looking value, an elevation
 * does.
 *
 * The table gives the run's *shape* in scanlines and says nothing about how
 * many degrees a scanline is worth, because the original's vertical field of
 * view has never been recovered — `setSkyShades`' body at `RB.EXE`
 * `3ED9:0020` is disassembled nowhere, and no capture of the original looking
 * *up* exists to solve it from.
 *
 * So the extent keeps the port's own calibration instead of inventing one:
 * `DAYTIME_HAZE_CONCENTRATION` was tuned until the run's half-way band sat at
 * **3.4 degrees**, which is what makes a level daytime view read as the flat
 * `#7db6c3` that `world-terrain.md` §7.1 measured. The recovered heights are
 * near enough symmetric that their half-way point is the middle of the run, so
 * twice 3.4 puts it in the same place — the shape becomes the original's while
 * the one free parameter stays where a measurement put it.
 *
 * **The wide-run question is deliberately left open, not silently answered.**
 * `enhanced-sky.md` §10.9 observes that 69 of 81 sky rows is a far broader haze
 * band than 3.4 degrees draws, and asks how that squares with §7.1's flat
 * measurement. It is not squared here: §7.1's own captures contain **no pixel
 * of 49-63 anywhere in frame**, so that mission appears not to have been
 * drawing the run at all — but that is an inference about an early return in
 * `sun.c`, not something anybody has verified, and widening this constant on
 * the strength of it would trade a measured picture for a plausible one.
 */
export const SKY_RUN_EXTENT_DEG = 6.8;
export const DAYTIME_SKY_COLOURS: readonly PaletteColour[] = [
  0x7db6c3, 0x7db6be, 0x82b6be, 0x86b6be, 0x8ab6be, 0x8eb6be, 0x92b6ba, 0x96b6ba, 0x9ab6ba, 0x9eb6ba, 0xa2b6ba,
  0xa6b6b6, 0xaab6b6, 0xaeb6b6, 0xb2b6b6, 0xb6b6b6,
];

/**
 * Dawn and dusk, as two real palette runs joined rather than as a mix between
 * one entry of each.
 *
 * The port's own description of these hours has always been "the twilight-blue
 * ramp (1-7) overhead and the sunset-orange ramp (243-254) at the skyline", and
 * before G18 that was a description of two *endpoints* with 14 invented colours
 * strung between them — a straight RGB line from `#416582` to `#ffaa5d` passes
 * through mauves and dirty pinks that `BARON.PAL` does not contain. Written out
 * as the entries, the run really is the two ramps: blue descending 7 -> 1
 * overhead, then orange climbing 243 -> 254 down at the skyline.
 *
 * Dusk starts deeper into the blue and stops short of the brightest orange,
 * which is what "dusk sits deeper into both than dawn" already meant and is why
 * its run is the shorter of the two. Both keep the exact endpoints they had.
 */
export const DAWN_SKY_RAMP: readonly number[] = [7, 6, 5, 4, 3, 2, 1, 243, 244, 245, 246, 247, 248, 249, 250, 251, 252, 253, 254];
export const DAWN_SKY_COLOURS: readonly PaletteColour[] = [
  0x416582, 0x385d75, 0x34516d, 0x304965, 0x284559, 0x243c51, 0x203449, 0x963800, 0x9e4104, 0xa64904, 0xb2510c,
  0xba5d14, 0xc3651c, 0xcf6d24, 0xd77930, 0xe38638, 0xeb9245, 0xf39e51, 0xffaa5d,
];
export const DUSK_SKY_RAMP: readonly number[] = [4, 3, 2, 1, 243, 244, 245, 246, 247, 248, 249, 250];
export const DUSK_SKY_COLOURS: readonly PaletteColour[] = [
  0x304965, 0x284559, 0x243c51, 0x203449, 0x963800, 0x9e4104, 0xa64904, 0xb2510c, 0xba5d14, 0xc3651c, 0xcf6d24,
  0xd77930,
];

/**
 * Night, and the sharpest illustration of what G18 was about: the run declared
 * **sixteen** bands between `BARON.PAL` 0 and `BARON.PAL` 1. There is nothing
 * between them — they are adjacent entries — so fourteen of those sixteen bands
 * were, of necessity, colours the game does not contain.
 *
 * Two entries is what the declared endpoints actually support, and two entries
 * across a `TWILIGHT_CONCENTRATION` run put the step at about 12 degrees of
 * elevation, which is a visible line rather than a wash. So the run is extended
 * *down its own palette ramp* instead: 0-4 is the head of the same 1-7 twilight
 * blue the dawn and dusk runs stand on, and `world/terrain-palette.ts` already
 * names 1-7 as a real run the game keeps (its `WATER_RAMP`). The horizon entry
 * therefore moves from 1 to 4 — the one endpoint G18 changes anywhere, and it
 * is changed because the old one could not be banded at all.
 */
export const NIGHT_SKY_RAMP: readonly number[] = [0, 1, 2, 3, 4];
export const NIGHT_SKY_COLOURS: readonly PaletteColour[] = [0x000000, 0x203449, 0x243c51, 0x284559, 0x304965];
